import React, {useEffect, useState} from "react";
import {useAuthInfo} from "@propelauth/react";
import {useParams} from "react-router-dom";
import {Download, ExternalLink, File, Link2, Trash2} from "lucide-react";
import {Button} from "../ui/button";
import {Spinner} from "../ui/spinner";
import {DragAndDropUploadv2} from "../files/DragAndUploadFile";
import {FileSecurityProps, FileView} from "../files/files";
import {getDownloadLink} from "../../api/filesView_api";
import {deleteWithToken, fetchWithToken} from "../../api/apiBase";
import {useToast} from "../ui/use-toast";

type BidFilesCardProps = {
    folder_id?: string;
    canEdit: boolean;
    onFilesLoaded?: (files: FileView[]) => void;
    // defaults are the bid - the scope card passes "scope" / scope_id
    parent_type?: string;
    parent_id?: string;
    emptyLabel?: string;
};

/**
 * A bid needs a place to hang the proposal, not a document library.
 * FilesView is still the full thing (folders, breadcrumbs, download all, links)
 * and is used everywhere else - this is the narrow panel that sits inside the
 * bid card (and the scope card). It renders no Card of its own.
 * */
export function BidFilesCard(
    {
        folder_id,
        canEdit,
        onFilesLoaded,
        parent_type = "bidding",
        parent_id,
        emptyLabel = "Drop your proposal here, or click to browse",
    }: BidFilesCardProps) {
    const {accessToken} = useAuthInfo();
    const {project_id, bid_id} = useParams<string>();
    const {toast} = useToast();
    const [files, setFiles] = useState<FileView[] | null>(null);

    const uploadContext: FileSecurityProps = {
        context: parent_type,
        permission: "view",
        project_id: project_id,
        parent_id: parent_id ? parent_id : bid_id as string,
        parent_type: parent_type,
        folder_id: folder_id,
    };

    const refresh = () => {
        // a bid with no root folder has nothing to list - don't spin forever.
        if (!folder_id) {
            setFiles([]);
            return;
        }
        fetchWithToken(`/files/list/v2/${folder_id}`, accessToken as string)
            .then((data) => {
                setFiles(data);
            });
    };

    useEffect(() => {
        refresh();
    }, [folder_id]);

    useEffect(() => {
        if (onFilesLoaded && files) {
            onFilesLoaded(files);
        }
    }, [files]);

    const onDelete = (file: FileView) => {
        deleteWithToken(
            `/files/delete/v1/${project_id}/${parent_type}/${file.id}`,
            accessToken as string
        )
            .then(() => refresh())
            .catch((err) => {
                toast({
                    title: "Error",
                    description: "Could not delete file: " + String(err),
                    variant: "destructive",
                });
            });
    };

    const onOpen = (file: FileView) => {
        if (file.is_link) {
            window.open(file.value as string, "_blank");
        } else {
            getDownloadLink(file, uploadContext, {accessToken: accessToken});
        }
    };

    const rows = files ? files.filter((f) => !f.is_folder) : [];

    return <div className="h-full flex flex-col">
        <div className="flex justify-between items-center mb-2">
            <div className="text-sm font-medium">Files</div>
            {rows.length > 0 &&
                <div className="text-xs text-gray-400">{rows.length}</div>
            }
        </div>

        {files === null && <div className="py-4"><Spinner size="small"/></div>}

        {files !== null && rows.length === 0 && !canEdit &&
            <div className="text-xs text-gray-400 py-2">No files attached.</div>
        }

        {/* with nothing attached the drop target is the whole panel */}
        {files !== null && rows.length === 0 && canEdit &&
            <DragAndDropUploadv2
                folder_id={folder_id as string}
                onAddFile={refresh}
                label={emptyLabel}
                className={"flex flex-col items-center justify-center text-center gap-1" +
                    " rounded-md border border-dashed border-gray-300 px-3 py-10" +
                    " text-xs text-gray-500 hover:border-gray-400 transition"}
            />
        }

        {rows.length > 0 &&
        <div className="flex-1 min-h-0 overflow-auto">
            {rows.map((file) => (
                <div
                    key={file.id}
                    className="flex items-center gap-1.5 py-1.5 border-t first:border-t-0"
                >
                    {file.is_link
                        ? <Link2 className="h-3.5 w-3.5 shrink-0 text-gray-400"/>
                        : <File className="h-3.5 w-3.5 shrink-0 text-gray-400"/>
                    }
                    <span
                        className="text-xs flex-1 min-w-0 truncate cursor-pointer hover:underline"
                        title={file.name}
                        onClick={() => onOpen(file)}
                    >
                        {file.name}
                    </span>
                    {/* type=button matters now - this panel lives inside the bid <form> */}
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-gray-400"
                        title={file.is_link ? "Open" : "Download"}
                        onClick={() => onOpen(file)}
                    >
                        {file.is_link
                            ? <ExternalLink className="h-3.5 w-3.5"/>
                            : <Download className="h-3.5 w-3.5"/>
                        }
                    </Button>
                    {canEdit &&
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 text-gray-400"
                            title="Delete"
                            onClick={() => onDelete(file)}
                        >
                            <Trash2 className="h-3.5 w-3.5"/>
                        </Button>
                    }
                </div>
            ))}
        </div>
        }

        {/* once something is attached the drop target shrinks out of the way */}
        {canEdit && rows.length > 0 && folder_id &&
            <div className="border-t pt-2 mt-auto">
                <DragAndDropUploadv2
                    folder_id={folder_id}
                    onAddFile={refresh}
                    label="Add file"
                    className={"flex items-center justify-center gap-1 rounded-md w-full" +
                        " border border-dashed border-gray-300 px-2 py-1" +
                        " text-[11px] text-gray-500 hover:border-gray-400 transition"}
                />
            </div>
        }
    </div>
}

export default BidFilesCard;
